/**
 * Promo été 2026 — IA offerte aux comptes créés jusqu’au 31 août 2026 inclus.
 * Accès valable jusqu’à fin février 2027, puis coupure automatique.
 *
 * - Bannière /pricing : visible seulement pendant la fenêtre d’inscription (jusqu’au 31 août)
 * - Accès app : si createdAt < 1er sept. 2026 → IA jusqu’au 28 févr. 2027 inclus
 */

/** Fin de la fenêtre d’inscription (31 août 2026 inclus, Europe/Paris). */
export const LYRRAONE_SUMMER_AI_PROMO_SIGNUP_ENDS_AT = new Date('2026-09-01T00:00:00+02:00');

/**
 * Coupure automatique de l’accès IA promo : fin février 2027 inclus
 * (désactivé à partir du 1er mars 2027, Europe/Paris — UTC+1 hors heure d’été).
 */
export const LYRRAONE_SUMMER_AI_PROMO_ACCESS_ENDS_AT = new Date('2027-03-01T00:00:00+01:00');

/** @deprecated Alias — fenêtre d’inscription / affichage marketing. */
export const LYRRAONE_SUMMER_AI_PROMO_ENDS_AT = LYRRAONE_SUMMER_AI_PROMO_SIGNUP_ENDS_AT;

/** Les 2 IA annoncées sur /pricing (+ formation simple pour que ce soit utilisable). */
export const LYRRAONE_SUMMER_AI_PROMO_FEATURES = [
  'designerAiBlocks',
  'simpleFormationAi',
  'expertAiAssistant',
] as const;

export type LyrraOneSummerAiPromoFeature =
  (typeof LYRRAONE_SUMMER_AI_PROMO_FEATURES)[number];

/** Bannière pricing / communication : ouverte jusqu’au 31 août 2026 inclus. */
export function isLyrraOneSummerAiPromoActive(now: Date = new Date()): boolean {
  return now.getTime() < LYRRAONE_SUMMER_AI_PROMO_SIGNUP_ENDS_AT.getTime();
}

export function isLyrraOneSummerAiPromoFeature(
  feature: string,
): feature is LyrraOneSummerAiPromoFeature {
  return (LYRRAONE_SUMMER_AI_PROMO_FEATURES as readonly string[]).includes(feature);
}

/**
 * Compte éligible s’il a été créé avant le 1er septembre 2026
 * (jusqu’au 31 août inclus).
 */
export function isLyrraOneSummerAiPromoEligibleAccount(
  accountCreatedAt: Date | string | null | undefined,
): boolean {
  if (!accountCreatedAt) return false;
  const created = accountCreatedAt instanceof Date
    ? accountCreatedAt
    : new Date(accountCreatedAt);
  if (Number.isNaN(created.getTime())) return false;
  return created.getTime() < LYRRAONE_SUMMER_AI_PROMO_SIGNUP_ENDS_AT.getTime();
}

/** Expiration fixe : fin février 2027 (null si non éligible). */
export function getLyrraOneSummerAiPromoExpiresAt(
  accountCreatedAt: Date | string | null | undefined,
): Date | null {
  if (!isLyrraOneSummerAiPromoEligibleAccount(accountCreatedAt)) return null;
  return new Date(LYRRAONE_SUMMER_AI_PROMO_ACCESS_ENDS_AT.getTime());
}

/** Accès app : feature IA débloquée jusqu’à fin février 2027 pour les comptes éligibles. */
export function summerAiPromoGrantsFeatureForAccount(
  accountCreatedAt: Date | string | null | undefined,
  feature: string,
  now: Date = new Date(),
): boolean {
  if (!isLyrraOneSummerAiPromoFeature(feature)) return false;
  if (!isLyrraOneSummerAiPromoEligibleAccount(accountCreatedAt)) return false;
  return now.getTime() < LYRRAONE_SUMMER_AI_PROMO_ACCESS_ENDS_AT.getTime();
}

/**
 * @deprecated Préférer `summerAiPromoGrantsFeatureForAccount`.
 * Pendant la fenêtre d’inscription uniquement (sans compte) — ex. fallback UI.
 */
export function summerAiPromoGrantsFeature(feature: string, now: Date = new Date()): boolean {
  return isLyrraOneSummerAiPromoActive(now) && isLyrraOneSummerAiPromoFeature(feature);
}
